import React from 'react';
import classnames from 'classnames/bind';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import { useTrackableItem } from '../../hooks/useTrackableItem';
import { useTrackableLists } from '../../hooks/useTrackableLists';
import { useUpdateTrackableItem } from '../../hooks/useUpdateTrackableItem';
import { useHasAccess } from 'hooks/useHasAccess';
import { TRACKABLE_OBJECT_TYPE_LABELS } from '../../utils';

import { NotFound } from 'views/NotFound/NotFound';

import {
  DetailView,
  DetailViewHeader,
  DetailViewStatus,
} from 'components/DetailView/DetailView';
import { Loader } from 'components/Loader/Loader';
import { Tabs, TabsButton, TabsContent, TabsList } from 'components/Tabs/Tabs';
import { Text } from 'components/Text/Text';

import { EditTrackableItemForm } from '../EditTrackableItemForm';
import { TrackableItemFiles } from './TrackableItemFiles';

import styles from './TrackableItemDetail.module.scss';

const c = classnames.bind(styles);

export function TrackableItemDetail() {
  const { applicationID, trackableListId, trackableItemId } = useParams<{
    applicationID: string;
    trackableListId: string;
    trackableItemId: string;
  }>();

  const location = useLocation();
  const navigate = useNavigate();
  const [hasAccess] = useHasAccess();
  const [isRenaming, toggleIsRenaming] = useToggle();

  // Check if we should open the upload dialog (from navigation state)
  const shouldOpenUpload = (location.state as any)?.openUpload === true;

  const handleUploadDialogOpened = React.useCallback(() => {
    // Clear the state after the upload dialog has been opened
    navigate(location.pathname, { replace: true, state: {} });
  }, [navigate, location.pathname]);

  const { data: trackableItem, isLoading: isItemLoading } = useTrackableItem({
    trackableListId,
    trackableItemId,
  });

  const { data: trackableListsData, isLoading: isListLoading } =
    useTrackableLists({
      params: { trackable_list_ids: trackableListId ? [trackableListId] : [] },
      enabled: Boolean(trackableListId),
    });

  const { mutate: updateTrackableItem } = useUpdateTrackableItem();

  const trackableList = trackableListsData?.data?.[0];

  if (isItemLoading || isListLoading) {
    return (
      <div className={c('loader')}>
        <Loader size="small" text="Loading..." />
      </div>
    );
  }

  if (!trackableItem || !trackableList) {
    return (
      <NotFound
        entity="watchlist item"
        returnTo={`/applications/${applicationID}/deploy/trackables/${trackableListId}`}
      />
    );
  }

  // After null check, we know trackableList exists
  const objectType = trackableList.object_type;
  const itemType = TRACKABLE_OBJECT_TYPE_LABELS[objectType].toLowerCase();
  const isVehicle = objectType === 'vehicle';

  function handleRename(name: string) {
    if (!trackableItem || !trackableListId || !trackableItemId) {
      return;
    }

    updateTrackableItem({
      trackable_list_id: trackableListId,
      trackable_item_id: trackableItemId,
      data: { name },
    });
  }

  return (
    <DetailView key={trackableItem.id} size="default">
      <DetailViewHeader
        entity={trackableItem}
        singleQueryKey={['trackable-item']}
        isRenaming={isRenaming}
        onRename={hasAccess('deploy_edit') ? handleRename : undefined}
        titleSuffix={<Text type="paragraph">{trackableList.name}</Text>}
      >
        <DetailViewStatus entity={trackableItem} onRename={toggleIsRenaming}>
          <div className={c('description')}>
            <Text type="paragraph">
              Manage {itemType} details{!isVehicle && ' and reference images'}.
            </Text>
          </div>
        </DetailViewStatus>
      </DetailViewHeader>

      <Tabs defaultValue={'details'} className={c('tabs')}>
        <TabsList className={c('tabs-list')}>
          <TabsButton value="details">Details</TabsButton>
          {!isVehicle && (
            <TabsButton value="files">Reference Images</TabsButton>
          )}
        </TabsList>

        {!isVehicle && (
          <TabsContent className={c('tab-content')} value="files">
            <TrackableItemFiles
              trackableItem={trackableItem}
              trackableListId={trackableListId!}
              openUploadOnMount={shouldOpenUpload}
              onUploadDialogOpened={handleUploadDialogOpened}
            />
          </TabsContent>
        )}

        <TabsContent className={c('tab-content')} value="details">
          <div className="flex h-full flex-col p-6">
            <EditTrackableItemForm
              trackableItem={trackableItem}
              trackableListId={trackableListId!}
              objectType={objectType}
              onSuccess={() => {
                navigate(
                  `/applications/${applicationID}/deploy/trackables/${trackableListId}`
                );
              }}
            />
          </div>
        </TabsContent>
      </Tabs>
    </DetailView>
  );
}
